import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { selectHasPermission } from "../store/slices/authSlice";
import MainLayout from "../components/layout/MainLayout";
import RolesList from "./Role/RolesList";
import Breadcrumbs from "../components/common/Breadcrumbs";
import {
  fetchRoles,
  fetchPermissions,
  deleteRole,
  clearError,
} from "../store/slices/rolesSlice";
import Swal from 'sweetalert2';


const Roles = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { roles, permissions, isLoading, error } = useSelector(
    (state) => state.roles
  );

  const canManageRoles = useSelector((state) =>
    selectHasPermission(state, "roles-manage")
  );
  const canCreateRoles = useSelector((state) =>
    selectHasPermission(state, "roles-create")
  );
  const canEditRoles = useSelector((state) =>
    selectHasPermission(state, "roles-edit")
  );
  const canDeleteRoles = useSelector((state) =>
    selectHasPermission(state, "roles-delete")
  );

  useEffect(() => {
    if (canManageRoles) {
      dispatch(fetchRoles());
      dispatch(fetchPermissions());
    }
  }, [dispatch, canManageRoles]);

  useEffect(() => {
    if (error) console.error("Roles error:", error);
  }, [error]);

  if (!canManageRoles) {
    return (
      <Navigate
        to="/dashboard"
        state={{ error: "You do not have permission to manage roles." }}
        replace
      />
    );
  }

  const handleCreateRole = () => navigate("/roles/add");

  const handleEditRole = (role) => navigate(`/roles/edit/${role.id}`);

  const handleDeleteRole = async (roleId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You want to delete this role?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteRole(roleId)).unwrap();
        dispatch(fetchRoles());
        Swal.fire('Deleted!', 'Role has been deleted.', 'success');
      } catch (err) {
        console.error("Failed to delete role:", err);
        Swal.fire('Error!', 'Failed to delete role.', 'error');
      }
    }
  };


  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error!</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => dispatch(clearError())}
              aria-label="Close"
            ></button>
          </div>
        )}

        <div className="card">
          <div className="card-body">
            {isLoading ? (
              <div className="d-flex justify-content-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <RolesList
                roles={roles}
                onEdit={canEditRoles ? handleEditRole : null}
                onDelete={canDeleteRoles ? handleDeleteRole : null}
                onAddRole={canCreateRoles ? handleCreateRole : null}
              />
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Roles;
